/**
 * Settings sections — the registry tables (./registry.ts). Each row is the one
 * declaration of a setting: default, bounds, label, hint. Stored values use the
 * stored unit; `scale` converts to the unit the Settings page shows.
 *
 * Bounds were reconciled 2026-10-06 from the three former sources (dashboard
 * field tables, server resolvers, none on save): a setting's range is what the
 * Settings page offered, narrowed to what the runtime accepts.
 */
import type { SettingsSection } from './registry.js';

/** Alert kinds (the alert engine's checks). */
export const ALERT_CATEGORIES = [
  'tool_error_rate',
  'routine_failures',
  'cost_ceiling',
  'tool_degraded',
  'capability_change',
] as const;

/** Routine engine (`scheduler`). */
export const SCHEDULER_SETTINGS = {
  maxConcurrent: {
    type: 'number',
    default: 3,
    min: 1,
    max: 10,
    integer: true,
    label: 'Max concurrent routines',
    hint: 'How many routines can run at the same time.',
  },
  maxConsecutiveFailures: {
    type: 'number',
    default: 5,
    min: 1,
    max: 50,
    integer: true,
    label: 'Max consecutive failures',
    hint: 'Automatically pause a routine after this many failures in a row.',
  },
  catchUpWindowMs: {
    type: 'number',
    default: 14_400_000,
    min: 0,
    max: 604_800_000,
    integer: true,
    label: 'Catch-up window',
    hint: 'After a restart, run missed routines within this window. Set to 0 to skip.',
    unit: 'hours',
    scale: 3_600_000,
  },
  maxRunsPerRoutine: {
    type: 'number',
    default: 50,
    min: 10,
    max: 1000,
    integer: true,
    label: 'Max run history per routine',
    hint: 'Run records kept per routine. Older runs are pruned.',
  },
  concurrencyRetryMs: {
    type: 'number',
    default: 30_000,
    min: 1000,
    max: 600_000,
    integer: true,
    label: 'Concurrency retry interval',
    hint: 'When all slots are busy, wait this long before checking the queue again.',
    unit: 'seconds',
    scale: 1000,
  },
  backoffBaseMs: {
    type: 'number',
    default: 30_000,
    min: 1000,
    max: 600_000,
    integer: true,
    label: 'Failure backoff base',
    hint: 'Base delay (seconds) for exponential backoff after a routine failure. AWS full-jitter pattern.',
    unit: 'seconds',
    scale: 1000,
  },
  backoffCapMs: {
    type: 'number',
    default: 1_800_000,
    min: 60_000,
    max: 7_200_000,
    integer: true,
    label: 'Failure backoff cap',
    hint: 'Maximum delay (minutes) for exponential backoff. Retry delay never exceeds this.',
    unit: 'minutes',
    scale: 60_000,
  },
} as const satisfies SettingsSection;

/** Browser session pool (`browser`). */
export const BROWSER_SETTINGS = {
  maxSessions: {
    type: 'number',
    default: 8,
    min: 1,
    max: 10,
    integer: true,
    label: 'Max concurrent sessions',
    hint: 'Hard cap on simultaneous Playwright contexts. Excess opens reject with a clear error.',
  },
  idleTimeoutMs: {
    type: 'number',
    default: 300_000,
    min: 60_000,
    max: 3_600_000,
    integer: true,
    label: 'Idle timeout',
    hint: 'Sessions idle longer than this get closed automatically.',
    unit: 'minutes',
    scale: 60_000,
  },
  settleAfterActionMs: {
    type: 'number',
    // A click or Enter may or may not start a navigation; this is how long
    // the tool waits for one to reach DOMContentLoaded before reporting the
    // page it is on. A wait for something that may never happen, so it is
    // short and not the call's limit.
    default: 5000,
    min: 0,
    max: 60_000,
    integer: true,
    label: 'Settle after click or Enter',
    hint: 'How long browser_click and browser_type (press_enter) wait for a page they may have started loading before reporting it. 0 = report at once.',
    unit: 'ms',
  },
} as const satisfies SettingsSection;

/** Agent loop (`agent`). */
export const AGENT_SETTINGS = {
  // Turn limits are runaway guards, not rationing: one rule on every executor
  // (API-key loop, Claude CLI, cloud), 0 = no limit (`turnLimit`).
  dashboardMaxTurns: {
    type: 'number',
    default: 0,
    min: 0,
    max: 1000,
    integer: true,
    label: 'Chat max turns',
    hint: 'Runaway guard for one chat reply, on every path. 0 = no limit (default) — you can Stop a reply.',
  },
  routineMaxTurns: {
    type: 'number',
    default: 200,
    min: 0,
    max: 1000,
    integer: true,
    label: 'Routine max turns',
    hint: 'Runaway guard for one routine run — nobody is watching it. 0 = no limit.',
  },
  routineMaxRunMs: {
    type: 'number',
    default: 3_600_000,
    min: 0,
    max: 86_400_000,
    integer: true,
    label: 'Routine max run time',
    hint: 'Runaway guard for one routine run, by the clock, on every path — nobody is watching it. 0 = no limit.',
    unit: 'minutes',
    scale: 60_000,
  },
  cliSilenceLimitMs: {
    type: 'number',
    default: 600_000,
    min: 30_000,
    max: 3_600_000,
    integer: true,
    label: 'Claude CLI silence limit',
    hint: 'A Claude subscription run that writes nothing for this long while no tool is running is treated as hung and stopped. Tool calls have their own time limits.',
    unit: 'minutes',
    scale: 60_000,
  },
  subagentMaxTurns: {
    type: 'number',
    // A worker's step budget when spawn_agent sets none (MA-471). Workers are
    // told the budget and batch their reads; at the limit they write a partial
    // report instead of failing, so a low default costs findings, not work.
    // The orchestrator raises it per call (`max_turns`) for a broad task.
    default: 30,
    min: 0,
    max: 1000,
    integer: true,
    label: 'Sub-agent max turns',
    hint: "A worker's step budget when spawn_agent sets no max_turns (an agent may set its own per call). At the limit the worker reports what it found, marked partial. 0 = no limit.",
  },
  subagentMaxConcurrent: {
    type: 'number',
    // 3–5 parallel workers is where Anthropic's multi-agent system got its
    // speed-up (anthropic.com/engineering/multi-agent-research-system); more
    // adds cost and machine load without a measured gain.
    default: 5,
    min: 1,
    max: 32,
    integer: true,
    label: 'Sub-agent max concurrent',
    hint: 'Hard cap on sub-agents running at once — parallelism vs. resource/cost. Further spawns are refused until a worker finishes.',
  },
  maxToolResultChars: {
    type: 'number',
    default: 100_000,
    min: 1000,
    max: 500_000,
    integer: true,
    label: 'Max tool-result chars',
    hint: 'Longest tool result that reaches the agent, on every path (a tool’s own Settings → Tools limit wins). Longer results are cut with a note. ~4 chars/token.',
  },
  persistToolResultChars: {
    type: 'number',
    default: 20_000,
    min: 1000,
    max: 200_000,
    integer: true,
    label: 'Persisted tool-result chars',
    hint: 'Per-result cap when writing tool results to the conversation log (cross-turn memory). Smaller than the in-turn cap — disk and every future replay pay for persisted chars.',
  },
  defaultToolTimeoutMs: {
    type: 'number',
    default: 120_000,
    min: 5000,
    max: 3_600_000,
    integer: true,
    label: 'Default tool timeout',
    hint: 'Per-tool execution timeout (ms). Overridden by per-tool policies in Settings → Tools. When it passes, the tool call is stopped, including any processes it started. Bash uses its own requested limit.',
    unit: 'ms',
  },
  subagentTimeoutMs: {
    type: 'number',
    default: 600_000,
    min: 60_000,
    max: 7_200_000,
    integer: true,
    label: 'Sub-agent timeout',
    hint: 'Sub-agent (spawn_agent) execution timeout (ms). Sub-agents run multi-turn loops — longer than single tools.',
    unit: 'ms',
  },
  providerRequestTimeoutMs: {
    type: 'number',
    apiKeyOnly: true,
    default: 120_000,
    min: 10_000,
    max: 600_000,
    integer: true,
    label: 'Provider request timeout',
    hint: 'How long an API provider may take to start answering before the attempt fails (and is retried). A streaming reply is never cut. Raise for slow local models or flaky networks.',
    unit: 'ms',
  },
  providerMaxRetries: {
    type: 'number',
    apiKeyOnly: true,
    default: 2,
    min: 0,
    max: 10,
    integer: true,
    label: 'Provider max retries',
    hint: 'Retry attempts for a transient provider failure (408/429/5xx, a dropped connection, no answer within the request timeout) before the turn fails. Exponential backoff with jitter, or the wait the provider asks for.',
  },
  providerMaxRetryWaitMs: {
    type: 'number',
    apiKeyOnly: true,
    default: 60_000,
    min: 1_000,
    max: 600_000,
    integer: true,
    label: 'Provider longest retry wait',
    hint: "The longest wait a provider may ask for (Retry-After) before a retry. Asked for longer — an hourly or daily quota — the turn fails at once with the provider's answer instead of hanging. Not yet applied to Anthropic on the desktop, whose SDK waits as asked (MA-346).",
    unit: 'ms',
  },
  askOperatorTimeoutMs: {
    type: 'number',
    default: 300_000,
    min: 30_000,
    max: 540_000,
    integer: true,
    label: 'Operator question timeout',
    hint: "How long ask_operator waits for an answer before the agent proceeds without one (ms). Capped at 9 min: the CLI path holds the tool call's HTTP request open for the whole wait.",
    unit: 'ms',
  },
  promptBudgetWarnTokens: {
    type: 'number',
    default: 30_000,
    min: 0,
    max: 100_000,
    integer: true,
    label: 'Prompt budget warn threshold',
    hint: 'System prompt token count above which a warning is logged. Indicates too many skills or instructions loaded.',
    unit: 'tokens',
  },
  promptBudgetHighTokens: {
    type: 'number',
    default: 60_000,
    min: 0,
    max: 200_000,
    integer: true,
    label: 'Prompt budget high threshold',
    hint: 'System prompt token count above which a stronger warning is logged. The agent still runs, but adherence degrades at this size.',
    unit: 'tokens',
  },
  budgetDowngradePct: {
    type: 'number',
    default: 80,
    min: 0,
    max: 100,
    integer: false,
    label: 'Budget auto-downgrade %',
    hint: 'When channel spend reaches this % of the daily cap, automatically downgrade to the fallback model. 0 = never downgrade.',
    unit: '%',
  },
  defaultReadFileLines: {
    type: 'number',
    default: 2000,
    min: 100,
    max: 50_000,
    integer: true,
    label: 'Default read_file lines',
    hint: 'Default max lines returned by read_file when the agent does not specify a limit.',
  },
  defaultGrepResults: {
    type: 'number',
    default: 100,
    min: 10,
    max: 10_000,
    integer: true,
    label: 'Default grep results',
    hint: 'Default max matches returned by grep when the agent does not specify max_results.',
  },
  maxReadFilesPerCall: {
    type: 'number',
    default: 20,
    min: 1,
    max: 100,
    integer: true,
    label: 'read_file: files per call',
    hint: 'Most files read_file reads in one call (its files list).',
  },
  maxGlobResults: {
    type: 'number',
    default: 500,
    min: 10,
    max: 10_000,
    integer: true,
    label: 'glob: max paths',
    hint: 'Most paths glob lists; the rest are counted.',
  },
  globLineCountMaxBytes: {
    type: 'number',
    default: 2_097_152,
    min: 0,
    max: 100_000_000,
    integer: true,
    label: 'glob details: line-count size limit (bytes)',
    hint: 'glob details counts lines only for files up to this size.',
  },
  maxGrepContextLines: {
    type: 'number',
    default: 20,
    min: 0,
    max: 200,
    integer: true,
    label: 'grep: max context lines',
    hint: 'Most lines grep shows around each match.',
  },
  grepMaxFileBytes: {
    type: 'number',
    // A file is read whole to search it; 10 MB is harmless, a multi-hundred-MB
    // log is not. Built bundles reach several MB (the desktop server is ~4 MB).
    default: 10_485_760,
    min: 0,
    max: 1_073_741_824,
    integer: true,
    label: 'grep: max file size (bytes)',
    hint: 'grep skips larger files and names them in its answer. 0 = no limit.',
  },
  searchMaxDepth: {
    type: 'number',
    default: 10,
    min: 1,
    max: 100,
    integer: true,
    label: 'grep / glob: max folder depth',
    hint: 'How many folder levels grep and glob search, the starting folder counting as one; deeper folders are named in the answer.',
  },
  maxLineChars: {
    type: 'number',
    default: 2000,
    min: 100,
    max: 100_000,
    integer: true,
    label: 'read_file / grep: max line length',
    hint: 'Longer lines (minified or data files) are cut, saying how many characters were left out.',
    unit: 'chars',
  },
  bashOutputMaxWaitSeconds: {
    type: 'number',
    default: 100,
    min: 0,
    max: 590,
    integer: true,
    label: 'bash_output: max wait (seconds)',
    hint: 'Longest a bash_output call waits for a background job (kept 10 s under the tool timeout).',
  },
  webSearchBlockPauseSeconds: {
    type: 'number',
    // DuckDuckGo's bot check refuses searches for a short spell; across 58 such
    // spells in the tool log (Sep–Oct 2026) searches worked again after a
    // median of 72 s. A search sent during one fails and can prolong it.
    default: 75,
    min: 0,
    max: 600,
    integer: true,
    label: 'web_search: pause after a bot check (seconds)',
    hint: 'After DuckDuckGo refuses searches, web_search answers "blocked" without asking it again for this long. 0 = ask again at once.',
  },
  webPageMaxBytes: {
    type: 'number',
    default: 10_485_760,
    min: 65_536,
    max: 104_857_600,
    integer: true,
    label: 'web_read / docs_index: largest page (bytes)',
    hint: 'A page is read up to this size; a longer one is cut there and the result says so. Bounds the memory one huge or endless response can take.',
  },
  docsSearchSectionChars: {
    type: 'number',
    default: 2_000,
    min: 200,
    max: 50_000,
    integer: true,
    label: 'docs_search: characters shown per section',
    hint: 'How much of each matching section docs_search shows. A longer section is cut there, and the result says how much was left out and where the whole page is.',
  },
} as const satisfies SettingsSection;

/** Memory and project retrieval depth (`retrieval`). */
export const RETRIEVAL_SETTINGS = {
  searchScanCeiling: {
    type: 'number',
    default: 500,
    min: 50,
    max: 10_000,
    integer: true,
    label: 'Search scan ceiling',
    hint: 'Most-recent entries the store scans for keyword ranking before returning hits. Entries past this are invisible to search — raise it if recall feels thin on a large store. The recall limiter.',
  },
  searchResultsDefault: {
    type: 'number',
    default: 15,
    min: 1,
    max: 100,
    integer: true,
    label: 'Search results (default)',
    hint: 'Results returned from memory_search when the agent does not pass an explicit limit.',
  },
  searchMaxResults: {
    type: 'number',
    default: 30,
    min: 1,
    max: 200,
    integer: true,
    label: 'Search results (hard cap)',
    hint: 'Ceiling on memory_search results — the agent cannot exceed this even by asking for more.',
  },
  listPageDefault: {
    type: 'number',
    default: 100,
    min: 1,
    max: 1000,
    integer: true,
    label: 'Internal list cap (default)',
    hint: "Default cap for internal full-list reads that don't specify a limit (e.g. the dashboard's memory/domain counts). Agent tools set their own page size and are unaffected.",
  },
  chatPageMessages: {
    type: 'number',
    default: 300,
    min: 20,
    max: 5000,
    integer: true,
    label: 'Chat page size',
    hint: 'Stored messages (each tool call and each result counts as one) the chat view loads when it opens a conversation, and per "Show earlier messages". Smaller opens long conversations faster, on the phone especially.',
  },
  channelLogReadChars: {
    type: 'number',
    default: 40_000,
    min: 2000,
    max: 500_000,
    integer: true,
    label: 'Channel log read size',
    hint: 'Most characters of a project channel log one knowledge_get returns — the newest days first. Logs keep their full history; an agent pages back to older days by date.',
  },
} as const satisfies SettingsSection;

/** Tool-I/O log (`toolIo`). */
export const TOOL_IO_SETTINGS = {
  resultCap: {
    type: 'number',
    default: 64_000,
    min: 1000,
    max: 1_000_000,
    integer: true,
    label: 'Tool result cap',
    hint: 'Max characters persisted per args/result field in the tool-I/O log (head+tail split, `truncated` flag on the record). Default 64000. Larger = more debug fidelity, more disk.',
    unit: 'chars',
  },
  retentionDays: {
    type: 'number',
    default: 30,
    min: 1,
    max: 365,
    integer: true,
    label: 'Tool-I/O retention',
    hint: 'How many days of tool-I/O history (args + result JSONL) are kept on disk. Same data queried by `tool_io_search`. Default 30 days.',
    unit: 'days',
  },
  searchSnippetChars: {
    type: 'number',
    default: 280,
    min: 80,
    max: 20_000,
    integer: true,
    label: 'Tool-I/O search preview',
    hint: 'Characters of args and result `tool_io_search` shows per record in a list. Each line names the record id; `id=<id>` or `full: true` returns the whole record. Default 280.',
    unit: 'chars',
  },
} as const satisfies SettingsSection;

/** Alert engine (`alerts`). 0 turns a threshold check off. */
export const ALERT_SETTINGS = {
  costCeilingUsd: {
    type: 'number',
    default: 0,
    min: 0,
    max: 1000,
    integer: false,
    label: 'Daily cost ceiling',
    hint: 'Dollar amount that triggers a cost alert. 0 = disabled. Covers all models and channels.',
    unit: '$',
    step: 5,
  },
  toolErrorRateThreshold: {
    type: 'number',
    default: 0.15,
    min: 0,
    max: 1,
    integer: false,
    label: 'Tool error rate threshold',
    hint: 'Overall tool error rate (0–1) that triggers a warning alert. Default 0.15 (15%). 0 = disabled.',
    step: 0.05,
  },
  toolDegradedThreshold: {
    type: 'number',
    default: 0.25,
    min: 0,
    max: 1,
    integer: false,
    label: 'Per-tool degraded threshold',
    hint: 'Individual tool error rate (0–1) that triggers a per-tool alert. Default 0.25 (25%). 0 = disabled.',
    step: 0.05,
  },
  routineFailureThreshold: {
    type: 'number',
    default: 3,
    min: 0,
    max: 20,
    integer: true,
    label: 'Routine failure threshold',
    hint: 'Number of consecutive routine failures before alerting. Default 3. 0 = disabled.',
  },
  intervalMinutes: {
    type: 'number',
    default: 5,
    min: 1,
    max: 60,
    integer: true,
    label: 'Alert check interval',
    hint: 'How often the alert engine re-evaluates thresholds. Default 5 minutes.',
    unit: 'min',
  },
  windowDays: {
    type: 'number',
    default: 1,
    min: 1,
    max: 30,
    integer: true,
    label: 'Alert lookback window',
    hint: 'How many days of data the alert engine considers. Default 1 (today only).',
    unit: 'days',
  },
  minCallsForSignal: {
    type: 'number',
    default: 5,
    min: 1,
    max: 1000,
    integer: true,
    label: 'Calls before error rates count',
    hint: 'Minimum tool calls in the window before an error rate can raise an alert.',
  },
  emailNotify: {
    type: 'boolean',
    default: false,
    label: 'Email alerts',
    hint: 'Email new alerts through the configured AgentMail inbox to the operator email.',
  },
  emailCategories: {
    type: 'list',
    default: ['routine_failures'],
    values: ALERT_CATEGORIES,
    label: 'Emailed alert kinds',
    hint: 'Which alert kinds are emailed when email alerts are on. Empty = none.',
  },
} as const satisfies SettingsSection;

/** Per-turn context section caps (`situation.sectionCaps`, chars). 0 = no cap. */
export const SITUATION_CAP_SETTINGS = {
  workingContext: {
    type: 'number',
    default: 2000,
    min: 0,
    max: 10_000,
    integer: true,
    label: 'Working context (chars)',
    hint: 'Overflow guard for cwd, git branch, working tree section.',
  },
  contextBudget: {
    type: 'number',
    default: 700,
    min: 0,
    max: 5000,
    integer: true,
    label: 'Context budget (chars)',
    hint: 'Overflow guard for the context-budget resource-awareness section.',
  },
} as const satisfies SettingsSection;

/** Per-turn context slice limits (`situation.sliceLimits`). 0 = no limit. */
export const SITUATION_SLICE_SETTINGS = {
  workingTreeLines: {
    type: 'number',
    default: 10,
    min: 0,
    max: 100,
    integer: true,
    label: 'Git status lines',
    hint: 'Max lines from `git status --short` shown in the working context. Prevents noise from dirty monorepos. 0 = show all.',
  },
  lastErrorLookbackMinutes: {
    type: 'number',
    default: 60,
    min: 0,
    max: 1440,
    integer: true,
    label: 'Last error look-back (minutes)',
    hint: "The turn note names the newest error logged since the conversation's previous turn, but none older than this. 0 = since the previous turn, however long ago.",
  },
} as const satisfies SettingsSection;

/** Agent scratch folder (`tmp`). */
export const TMP_SETTINGS = {
  retentionHours: {
    type: 'number',
    default: 48,
    min: 1,
    max: 720,
    integer: true,
    label: 'Tmp retention',
    hint: 'Files in ~/.onestone/tmp/ (unsent chat uploads, browser, desktop, and agent scratch) older than this are swept on app boot + every 6h. Files sent with a message live with their conversation and are never swept. Default 48h while active coding; raise for longer-running scratch work.',
    unit: 'hours',
  },
} as const satisfies SettingsSection;

/** The client for external MCP servers (`mcpClient`; the servers themselves are `externalMcp`). */
export const MCP_CLIENT_SETTINGS = {
  reconnectBaseMs: {
    type: 'number',
    default: 2000,
    min: 100,
    max: 60_000,
    integer: true,
    label: 'Reconnect first wait',
    hint: 'How long OneStone waits before reconnecting an external MCP server that dropped or failed to connect. Each failed try doubles the wait, up to the longest wait below.',
    unit: 'ms',
  },
  reconnectMaxMs: {
    type: 'number',
    default: 300_000,
    min: 1000,
    max: 3_600_000,
    integer: true,
    label: 'Reconnect longest wait',
    hint: 'The most time between reconnect tries for a server that stays down. A server command runs again on every try, so keep this long enough not to load the machine.',
    unit: 'ms',
  },
  instructionsMaxChars: {
    type: 'number',
    default: 2048,
    min: 0,
    max: 20_000,
    integer: true,
    label: 'Server instructions cap',
    hint: 'Most characters of one server’s own instructions the agent is given, marked as third-party text. 0 = none are given. Status always shows the full length.',
    unit: 'chars',
  },
} as const satisfies SettingsSection;

/** Claude CLI working files (`claudeHome`). */
export const CLAUDE_HOME_SETTINGS = {
  retentionHours: {
    type: 'number',
    default: 168,
    min: 24,
    max: 8760,
    integer: true,
    label: 'Claude CLI session retention',
    hint: 'Files under ~/.onestone/claude-home/ (env caches, shell snapshots, tasks, backups; conversation transcripts live with their conversation) older than this are swept on app boot + every 6h. Protected files (.claude.json, .credentials.json, mcp-needs-auth-cache.json) are never touched. Default 168h (7d).',
    unit: 'hours',
  },
} as const satisfies SettingsSection;

/** Top-level retention settings (`opsRetentionDays`, `trashRetentionDays`). */
export const RETENTION_SETTINGS = {
  opsRetentionDays: {
    type: 'number',
    default: 30,
    min: 7,
    max: 365,
    integer: true,
    label: 'Operations log retention',
    hint: 'How many days of agent activity data (tool calls, turns, routine runs, errors) are kept on disk. This is the data source for analytics, insights, and the activity feed. Default 30 days.',
    unit: 'days',
  },
  trashRetentionDays: {
    type: 'number',
    default: 7,
    min: 0,
    max: 365,
    integer: true,
    label: 'Trash retention',
    hint: 'How many days anything deleted, and the previous version of anything edited, stays in the trash before it is permanently removed. The purge runs on boot and every 24h. Default 7 days. 0 turns the automatic purge off.',
    unit: 'days',
  },
  trashSkipFolders: {
    type: 'list',
    default: [
      'node_modules',
      'dist',
      'build',
      'out',
      'release',
      'coverage',
      '.cache',
      '.vitest',
      '.wrangler',
      '.astro',
      '.next',
      '.turbo',
      'target',
      'test-results',
      'playwright-report',
      '__pycache__',
      '.mypy_cache',
      '.pytest_cache',
      '.ruff_cache',
      '.hypothesis',
      '.venv',
      'venv',
    ],
    label: 'Folders the trash skips',
    hint: "Folder names (at any depth in a project) whose files are not kept in the trash when an agent changes them — generated output that tools rebuild: dependencies, build output, caches. A project's .git is always skipped. A project can skip more of its own paths in its details.",
  },
} as const satisfies SettingsSection;

/**
 * A long conversation's context (`context`). Only the operator condenses a
 * conversation (Summarize); OneStone warns as the window fills (ADR-0002
 * amendment 2026-10-16b).
 */
export const CONTEXT_SETTINGS = {
  warnAtPct: {
    type: 'number',
    // A share of the model's window, not a fixed size: windows range from
    // 200K to 1M.
    default: 70,
    min: 30,
    max: 95,
    integer: true,
    step: 5,
    unit: '%',
    label: 'Warn when the context is',
    hint: "When the last prompt fills this share of the model's context window, the agent tells you and suggests Summarize, and the chat's turn bar shows it. Nothing is condensed until you summarize.",
  },
} as const satisfies SettingsSection;

/** Summarize pipeline (`summarize`). */
export const SUMMARIZE_SETTINGS = {
  chunkChars: {
    type: 'number',
    default: 300_000,
    min: 0,
    integer: true,
    label: 'Chunk size (chars)',
    hint: 'Max conversation content per summarize call. Smaller = more chunks (more calls, lighter prompts). 0 = no chunking — always a single pass.',
  },
  strategy: {
    type: 'enum',
    default: 'auto',
    values: ['auto', 'iterative', 'map-refine'],
    label: 'Multi-chunk strategy',
    hint: 'iterative = one chunk after another (best continuity); map-refine = chunks in parallel, then one merge; auto = iterative below the map-refine threshold.',
  },
  mapRefineMinChunks: {
    type: 'number',
    default: 3,
    min: 2,
    integer: true,
    label: 'Map-refine threshold (chunks)',
    hint: 'In Auto mode, runs at or above this many chunks switch to map-refine.',
  },
  mapConcurrency: {
    type: 'number',
    default: 3,
    min: 1,
    max: 16,
    integer: true,
    label: 'Map parallelism',
    hint: 'Max chunk summaries in flight at once during map-refine (1–16).',
  },
  toolResultChars: {
    type: 'number',
    default: 8_000,
    min: 1,
    integer: true,
    label: 'Tool result chars',
    hint: 'Max chars of a single tool result in the summarize prompt. Head+tail truncation preserves both call structure and error output.',
  },
  thinkingChars: {
    type: 'number',
    default: 4_000,
    min: 1,
    integer: true,
    label: 'Thinking block chars',
    hint: 'Max chars of a thinking/reasoning block. Lower than tool results — reasoning is supplementary context.',
  },
  minWords: {
    type: 'number',
    default: 150,
    min: 1,
    integer: true,
    label: 'Min summary words',
    hint: 'Word floor for short conversations.',
  },
  maxWords: {
    type: 'number',
    default: 500,
    min: 1,
    integer: true,
    label: 'Max summary words',
    hint: 'Word ceiling for marathon sessions.',
  },
  scaleLowChars: {
    type: 'number',
    default: 30_000,
    min: 1,
    integer: true,
    label: 'Scale start (chars)',
    hint: 'Conversation size below which word budget stays at minimum.',
  },
  scaleHighChars: {
    type: 'number',
    default: 500_000,
    min: 1,
    integer: true,
    label: 'Scale ceiling (chars)',
    hint: 'Conversation size at which word budget reaches maximum.',
  },
  idleTimeoutMs: {
    type: 'number',
    default: 90_000,
    min: 1,
    integer: true,
    label: 'Idle timeout (ms)',
    hint: 'Inter-token silence budget. A stalled summarize is killed after this. Active streaming resets the timer.',
  },
  firstOutputTimeoutMs: {
    type: 'number',
    default: 300_000,
    min: 1,
    integer: true,
    label: 'First output timeout (ms)',
    hint: 'Pre-first-token budget. Covers silent prefill of large prompts + upstream retries.',
  },
} as const satisfies SettingsSection;
